import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Download, Calendar as CalendarIcon, FileText, Loader2, 
  CheckCircle2, X, Clock, ChevronRight
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

export const ExportPage = () => {
  const { api } = useAuth();
  const [posts, setPosts] = useState([]);
  const [selectedPosts, setSelectedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [startTime, setStartTime] = useState('09:00');
  const [interval, setInterval] = useState(24);
  const [calendarOpen, setCalendarOpen] = useState(false);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await api.get('/posts?limit=200');
        setPosts(response.data.posts);
      } catch (error) {
        toast.error('Failed to load posts');
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [api]);

  const togglePost = (postId) => {
    setSelectedPosts(prev =>
      prev.includes(postId)
        ? prev.filter(id => id !== postId)
        : [...prev, postId]
    );
  };

  const selectAll = () => {
    setSelectedPosts(posts.map(p => p.id));
  };

  const clearSelection = () => {
    setSelectedPosts([]);
  };

  const handleExport = async () => {
    if (selectedPosts.length === 0) {
      toast.error('Please select at least one post');
      return;
    }

    setExporting(true);
    try {
      const response = await api.post('/export/csv', {
        post_ids: selectedPosts,
        start_date: format(startDate, 'yyyy-MM-dd'),
        start_time: startTime,
        interval_hours: interval
      });

      // Download the CSV
      const blob = new Blob([response.data.csv_content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success(`Exported ${selectedPosts.length} posts!`);
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="export-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900">Export Content</h1>
        <p className="text-zinc-500">Export your posts to CSV for scheduling tools</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Settings Panel */}
        <div className="lg:col-span-1 space-y-4">
          <Card className="border-zinc-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Export Settings</CardTitle>
              <CardDescription>Configure your export schedule</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      data-testid="start-date-btn"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(startDate, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={(date) => {
                        if (date) {
                          setStartDate(date);
                          setCalendarOpen(false);
                        }
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  data-testid="start-time-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Posting Interval</Label>
                <Select value={interval.toString()} onValueChange={(v) => setInterval(parseInt(v))}>
                  <SelectTrigger data-testid="interval-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="6">Every 6 hours</SelectItem>
                    <SelectItem value="12">Every 12 hours</SelectItem>
                    <SelectItem value="24">Once daily</SelectItem>
                    <SelectItem value="48">Every 2 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="pt-4 border-t border-zinc-100">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-500">Selected posts</span>
                  <span className="font-medium text-zinc-900">{selectedPosts.length}</span>
                </div>
              </div>

              <Button
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                onClick={handleExport}
                disabled={selectedPosts.length === 0 || exporting}
                data-testid="export-btn"
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Export CSV
              </Button>
            </CardContent>
          </Card>

          {/* CSV Preview */}
          <Card className="border-zinc-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-zinc-500">CSV Format Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-zinc-50 rounded-lg p-3 font-mono text-xs text-zinc-600 overflow-x-auto">
                <p>date,time,platform,caption,hashtags,image_prompt</p>
                <p className="text-zinc-400 mt-1">{format(startDate, 'yyyy-MM-dd')},{startTime},instagram,"Caption...","#hashtag...","Prompt..."</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Posts Selection */}
        <div className="lg:col-span-2">
          <Card className="border-zinc-200 h-full">
            <CardHeader className="pb-3 border-b border-zinc-100">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Select Posts</CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={selectAll} data-testid="select-all-btn">
                    Select All
                  </Button>
                  <Button variant="outline" size="sm" onClick={clearSelection} data-testid="clear-selection-btn">
                    Clear
                  </Button>
                </div>
              </div>
            </CardHeader>
            <ScrollArea className="h-[500px]">
              <CardContent className="p-4">
                {loading ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                  </div>
                ) : posts.length === 0 ? (
                  <div className="text-center py-20">
                    <FileText className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                    <p className="text-zinc-600 font-medium">No posts to export</p>
                    <p className="text-zinc-400 text-sm mt-1">Generate some content first</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {posts.map((post) => (
                      <div
                        key={post.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                          selectedPosts.includes(post.id)
                            ? 'border-indigo-500 bg-indigo-50/50'
                            : 'border-zinc-200 hover:border-zinc-300'
                        }`}
                        onClick={() => togglePost(post.id)}
                        data-testid={`export-post-${post.id}`}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          selectedPosts.includes(post.id)
                            ? 'border-indigo-600 bg-indigo-600'
                            : 'border-zinc-300'
                        }`}>
                          {selectedPosts.includes(post.id) && (
                            <CheckCircle2 className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-zinc-900 truncate">{post.post_title}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs bg-zinc-100 px-2 py-0.5 rounded">{post.platform}</span>
                            <span className="text-xs text-zinc-400">{post.post_type}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </ScrollArea>
          </Card>
        </div>
      </div>
    </div>
  );
};
